import {ChangeDetectorRef, Component, inject, OnInit, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {NgbModal, NgbPagination} from '@ng-bootstrap/ng-bootstrap';
import {ThaiDateTimePipe} from '../../../shared/commons/thai-datetime.pipe';

import {firstValueFrom} from 'rxjs';
import {DepartmentId, StandardCodeDto, StandardCodeId} from '../../../../generated-model/models';
import {StandardCodeService} from './standard-code-service';
import {StandardCodeModal} from './standard-code-modal';
import {ConfirmDialogService} from '../../../shared/ui/confirm-dialog/confirm-dialog.service';

import {resolveTotalElements} from '../../../shared/commons/pagination.util';
import {PageTitleComponent} from '../../../shared/commons/page-title/page-title.component';
import {PagesizeSelectComponent} from '../../../shared/components/pagesize-select/pagesize-select.component';
import {StatusBadgeComponent} from '../../../shared/components/status-badge/status-badge.component';
import {SearchIconComponent} from '../../../shared/components/search-icon/search-icon.component';

@Component({
  selector: 'app-standard-code',
  standalone: true,
  imports: [CommonModule, FormsModule, NgbPagination, ThaiDateTimePipe, PageTitleComponent, PagesizeSelectComponent, StatusBadgeComponent, SearchIconComponent],
  templateUrl: './standard-code.html',
  styleUrl: './standard-code.scss',
})
export class StandardCodeComponent implements OnInit {
  private readonly service = inject(StandardCodeService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly modalService = inject(NgbModal);
  private readonly confirmDialogService = inject(ConfirmDialogService);

  protected readonly searchQuery = signal('');
  protected readonly stateFilter = signal<number | null>(null);
  protected readonly departmentFilter = signal<DepartmentId | null>(null);
  protected readonly isLoading = signal(false);
  protected readonly page = signal(1);
  protected readonly pageSize = signal(10);
  protected readonly collectionSize = signal(0);
  protected readonly displayedItems = signal<StandardCodeDto[]>([]);

  private totalElements = 0;

  constructor() {
  }

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.isLoading.set(true);
    this.service.getAllStandardCodes(this.page() - 1, this.pageSize(), this.searchQuery(), this.stateFilter(), this.departmentFilter()).subscribe({
      next: (data) => {
        this.displayedItems.set(data.content);
        this.totalElements = resolveTotalElements(data);
        this.collectionSize.set(this.totalElements);
        this.isLoading.set(false);
        this.cdr.detectChanges();
      },
      error: () => {
        this.isLoading.set(false);
        this.cdr.detectChanges();
      }
    });
  }

  onSearchChange(): void {
    this.page.set(1);
    this.load();
  }

  clearSearch(): void {
    this.searchQuery.set('');
    this.stateFilter.set(null);
    this.departmentFilter.set(null);
    this.onSearchChange();
  }

  getStandardCodeDisplay(item: StandardCodeDto): string {
    const preCode = (item.preCode ?? '').trim();
    const standardCode = (item.standardCode ?? '').trim();
    const fullCode = `${preCode} ${standardCode}`.trim();
    return fullCode || '-';
  }

  openAddModal(): void {
    const modalRef = this.modalService.open(StandardCodeModal, {size: 'xl'});
    modalRef.componentInstance.onSave = async (result: StandardCodeDto): Promise<boolean> => {
      const confirmed = await this.confirmDialogService.confirmSave();
      if (!confirmed) {
        return false;
      }

      try {
        await firstValueFrom(this.service.createStandardCode(result));
        this.load();
        return true;
      } catch {
        return false;
      }
    };
  }

  openEditModal(item: StandardCodeDto): void {
    const modalRef = this.modalService.open(StandardCodeModal, {size: 'xl'});
    modalRef.componentInstance.item = item;
    modalRef.componentInstance.onSave = async (result: StandardCodeDto): Promise<boolean> => {
      const confirmed = await this.confirmDialogService.confirmSave();
      if (!confirmed || result.id == null) {
        return false;
      }
      try {
        await firstValueFrom(this.service.updateStandardCode(result.id, result));
        this.load();
        return true;
      } catch {
        return false;
      }
    };
  }

  openViewModal(item: StandardCodeDto): void {
    const modalRef = this.modalService.open(StandardCodeModal, {size: 'xl'});
    modalRef.componentInstance.item = item;
    modalRef.componentInstance.viewOnly = true;
  }

  async delete(id: StandardCodeId): Promise<void> {
    const confirmed = await this.confirmDialogService.confirmDelete('คุณต้องการลบข้อมูลนี้ใช่หรือไม่?');
    if (!confirmed) {
      return;
    }
    this.service.deleteStandardCode(id).subscribe(() => this.load());
  }
}
